// Common Translations for Cart, Checkout, UserProfile, ReceiptUpload and other common elements
export const commonTranslations = {
  en: {
    // ==================== CART PAGE ====================
    cart: {
      pageTitle: 'Shopping Cart',
      itemsInCart: 'items in your cart',
      itemInCart: 'item in your cart',
      noItemsTitle: 'No Items in Cart Yet',
      noItemsDesc: 'Start shopping to add products to your cart',
      browseProducts: 'Browse Products',
      yearly: 'Yearly',
      monthly: 'Monthly',
      yearlyPlan: 'Yearly Plan',
      monthlyPlan: 'Monthly Plan',
      remove: 'Remove',
      deleteTitle: 'Delete',
      deleteMessage: 'Are you sure you want to delete this Cart item?',
      delete: 'Delete',
      cancel: 'Cancel',
      // Order Summary
      orderSummary: 'Order Summary',
      subtotal: 'Subtotal',
      items: 'items',
      shipping: 'Shipping',
      calculatedAtCheckout: 'Calculated at checkout',
      taxes: 'Taxes',
      total: 'Total',
      proceedToCheckout: 'Proceed to Checkout',
      continueShopping: 'Continue Shopping',
    },

    // ==================== CHECKOUT PAGE ====================
    checkout: {
      pageTitle: 'Checkout',
      pageSubtitle: 'Complete your order details',
      // Personal Information
      personalInfo: 'Personal Information',
      personalInfoDesc: 'Your details for delivery',
      fullName: 'Full Name',
      fullNamePlaceholder: 'John Doe',
      fullNameRequired: 'Name is required',
      emailAddress: 'Email Address',
      emailPlaceholder: 'john@example.com',
      emailRequired: 'Email is required',
      contactNumber: 'Contact Number',
      contactPlaceholder: '+92-XXX-XXXXXXX',
      contactRequired: 'Contact number is required',
      // Subscription Type
      subscriptionType: 'Subscription Type',
      myAccountOption: 'Provide subscription on my account',
      myAccountOptionDesc: 'We will use your account credentials for the subscription',
      providedOption: 'Provide credentials from your side',
      providedOptionDesc: 'We will create and manage the account for you',
      // Account Credentials
      accountUsername: 'Account Username / Email',
      accountUsernamePlaceholder: 'Service account email',
      accountUsernameRequired: 'Account username/email is required',
      accountPassword: 'Account Password',
      accountPasswordPlaceholder: 'Service account password',
      accountPasswordRequired: 'Account password is required',
      // Actions
      reset: 'Reset',
      addAddress: 'Add Address',
      // Delivery Address
      deliveryAddress: 'Delivery Address',
      deliveryAddressDesc: 'Choose from saved addresses',
      noSavedAddresses: 'No saved addresses. Please add one above.',
      // Payment Method
      paymentMethod: 'Payment Method',
      paymentMethodDesc: 'Select your preferred payment option',
      bankTransfer: 'Bank Transfer / Receipt Upload',
      bankTransferDesc: 'Make payment and upload your receipt for verification',
      // Order Summary
      orderSummary: 'Order Summary',
      itemsInCart: 'items in your cart',
      subtotal: 'Subtotal',
      shipping: 'Shipping',
      calculated: 'Calculated',
      total: 'Total',
      placeOrder: 'Place Order',
      continueShopping: 'Continue Shopping',
      // Alerts
      alertAddressPayment: 'Please select a delivery address and payment method',
      alertPayment: 'Please select a payment method',
    },

    // ==================== USER PROFILE PAGE ====================
    profile: {
      admin: 'Admin',
      savedAddresses: 'Saved Addresses',
      savedAddressesDesc: 'Manage your delivery addresses',
      addAccount: 'Add Account',
      // Form Labels
      personalInfo: 'Personal Information',
      fullName: 'Full Name',
      fullNamePlaceholder: 'John Doe',
      fullNameRequired: 'Name is required',
      emailAddress: 'Email Address',
      emailPlaceholder: 'john@example.com',
      emailRequired: 'Email is required',
      contactNumber: 'Contact Number',
      contactPlaceholder: '+92-XXX-XXXXXXX',
      contactRequired: 'Contact number is required',
      // Account Information
      accountInfo: 'Account Information',
      accountUsername: 'Account Username / Email',
      accountUsernamePlaceholder: 'Service account email',
      accountUsernameRequired: 'Account username is required',
      accountPassword: 'Account Password',
      accountPasswordPlaceholder: 'Service account password',
      accountPasswordRequired: 'Account password is required',
      // Actions
      cancel: 'Cancel',
      saveChanges: 'Save Changes',
      edit: 'Edit',
      remove: 'Remove',
      // Empty State
      noAddresses: 'No addresses saved yet',
      noAddressesDesc: 'Add an address to get started',
      // Labels
      account: 'Account',
      email: 'Email',
    },

    // ==================== RECEIPT UPLOAD PAGE ====================
    receipt: {
      pageTitle: 'Upload Payment Receipt',
      orderTotal: 'Order Total',
      orderId: 'Order ID',
      items: 'Items',
      itemsCount: 'item(s)',
      // Instructions
      instructionsTitle: 'Payment Instructions',
      instruction1: 'Make payment to the specified account',
      instruction2: 'Take a clear screenshot or photo of your payment receipt',
      instruction3: 'Upload the receipt image below',
      instruction4: 'Wait for our team to verify your payment',
      // Upload Area
      dragDrop: 'Drag and drop your receipt here',
      orClickBrowse: 'or click to browse',
      supportedFormats: 'Supports: JPG, PNG, WEBP (Max 5MB)',
      removeChooseAnother: 'Remove & Choose Another',
      // Errors
      selectImageError: 'Please select an image file (JPG, PNG, etc.)',
      fileSizeError: 'File size must be less than 5MB',
      selectReceiptError: 'Please select a receipt image first',
      uploadFailed: 'Failed to upload receipt',
      networkError: 'Network error. Please try again.',
      // Buttons
      uploading: 'Uploading...',
      uploadButton: 'Upload Receipt & Complete Order',
      backToCheckout: 'Back to Checkout',
      // Security Note
      securityNote: 'Your receipt will be securely stored and reviewed by our team within 24 hours.',
      // Success
      successTitle: 'Receipt Uploaded Successfully!',
      successDesc: 'Your payment receipt has been submitted for verification. We\'ll notify you once it\'s verified.',
      redirecting: 'Redirecting to order confirmation...',
    },

    // ==================== ORDER SUCCESS PAGE ====================
    orderSuccess: {
      pageTitle: 'Order Submitted!',
      orderNumber: 'Order',
      thankYouMessage: 'Thank you for your order. Your payment receipt has been submitted and is pending verification by our team.',
      whatsNext: "What's Next?",
      step1: 'Our team will verify your payment receipt within 24 hours.',
      step2: "You'll receive an email notification once your payment is verified.",
      step3: 'After verification, your subscription will be activated and delivered.',
      step4: 'Track your order status in My Account > My Orders.',
      backToHome: 'Back to Home',
      viewMyOrders: 'View My Orders',
      supportNote: 'Questions? Contact our support team for assistance',
    },

    // ==================== COMMON ELEMENTS ====================
    common: {
      loading: 'Loading...',
      error: 'Error',
      success: 'Success',
      save: 'Save',
      cancel: 'Cancel',
      delete: 'Delete',
      edit: 'Edit',
      add: 'Add',
      remove: 'Remove',
      close: 'Close',
      confirm: 'Confirm',
      back: 'Back',
      next: 'Next',
      submit: 'Submit',
      search: 'Search',
      filter: 'Filter',
      sort: 'Sort',
      all: 'All',
      yes: 'Yes',
      no: 'No',
      or: 'or',
      and: 'and',
      required: 'Required',
      optional: 'Optional',
    },
  },

  fr: {
    // ==================== CART PAGE ====================
    cart: {
      pageTitle: 'Panier',
      itemsInCart: 'articles dans votre panier',
      itemInCart: 'article dans votre panier',
      noItemsTitle: 'Aucun Article dans le Panier',
      noItemsDesc: 'Commencez vos achats pour ajouter des produits à votre panier',
      browseProducts: 'Parcourir les Produits',
      yearly: 'Annuel',
      monthly: 'Mensuel',
      yearlyPlan: 'Forfait Annuel',
      monthlyPlan: 'Forfait Mensuel',
      remove: 'Supprimer',
      deleteTitle: 'Supprimer',
      deleteMessage: 'Êtes-vous sûr de vouloir supprimer cet article du panier ?',
      delete: 'Supprimer',
      cancel: 'Annuler',
      // Order Summary
      orderSummary: 'Résumé de la Commande',
      subtotal: 'Sous-total',
      items: 'articles',
      shipping: 'Livraison',
      calculatedAtCheckout: 'Calculé à la caisse',
      taxes: 'Taxes',
      total: 'Total',
      proceedToCheckout: 'Passer à la Caisse',
      continueShopping: 'Continuer les Achats',
    },

    // ==================== CHECKOUT PAGE ====================
    checkout: {
      pageTitle: 'Paiement',
      pageSubtitle: 'Complétez les détails de votre commande',
      // Personal Information
      personalInfo: 'Informations Personnelles',
      personalInfoDesc: 'Vos coordonnées pour la livraison',
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Jean Dupont',
      fullNameRequired: 'Le nom est requis',
      emailAddress: 'Adresse Email',
      emailPlaceholder: 'jean@exemple.com',
      emailRequired: 'L\'email est requis',
      contactNumber: 'Numéro de Contact',
      contactPlaceholder: '+33-XXX-XXXXXXX',
      contactRequired: 'Le numéro de contact est requis',
      // Subscription Type
      subscriptionType: 'Type d\'Abonnement',
      myAccountOption: 'Fournir l\'abonnement sur mon compte',
      myAccountOptionDesc: 'Nous utiliserons les identifiants de votre compte pour l\'abonnement',
      providedOption: 'Fournir les identifiants de votre côté',
      providedOptionDesc: 'Nous créerons et gérerons le compte pour vous',
      // Account Credentials
      accountUsername: 'Nom d\'Utilisateur / Email du Compte',
      accountUsernamePlaceholder: 'Email du compte de service',
      accountUsernameRequired: 'Le nom d\'utilisateur/email du compte est requis',
      accountPassword: 'Mot de Passe du Compte',
      accountPasswordPlaceholder: 'Mot de passe du compte de service',
      accountPasswordRequired: 'Le mot de passe du compte est requis',
      // Actions
      reset: 'Réinitialiser',
      addAddress: 'Ajouter une Adresse',
      // Delivery Address
      deliveryAddress: 'Adresse de Livraison',
      deliveryAddressDesc: 'Choisissez parmi les adresses enregistrées',
      noSavedAddresses: 'Aucune adresse enregistrée. Veuillez en ajouter une ci-dessus.',
      // Payment Method
      paymentMethod: 'Méthode de Paiement',
      paymentMethodDesc: 'Sélectionnez votre option de paiement préférée',
      bankTransfer: 'Virement Bancaire / Téléchargement du Reçu',
      bankTransferDesc: 'Effectuez le paiement et téléchargez votre reçu pour vérification',
      // Order Summary
      orderSummary: 'Résumé de la Commande',
      itemsInCart: 'articles dans votre panier',
      subtotal: 'Sous-total',
      shipping: 'Livraison',
      calculated: 'Calculé',
      total: 'Total',
      placeOrder: 'Passer la Commande',
      continueShopping: 'Continuer les Achats',
      // Alerts
      alertAddressPayment: 'Veuillez sélectionner une adresse de livraison et une méthode de paiement',
      alertPayment: 'Veuillez sélectionner une méthode de paiement',
    },

    // ==================== USER PROFILE PAGE ====================
    profile: {
      admin: 'Administrateur',
      savedAddresses: 'Adresses Enregistrées',
      savedAddressesDesc: 'Gérez vos adresses de livraison',
      addAccount: 'Ajouter un Compte',
      // Form Labels
      personalInfo: 'Informations Personnelles',
      fullName: 'Nom Complet',
      fullNamePlaceholder: 'Jean Dupont',
      fullNameRequired: 'Le nom est requis',
      emailAddress: 'Adresse Email',
      emailPlaceholder: 'jean@exemple.com',
      emailRequired: 'L\'email est requis',
      contactNumber: 'Numéro de Contact',
      contactPlaceholder: '+33-XXX-XXXXXXX',
      contactRequired: 'Le numéro de contact est requis',
      // Account Information
      accountInfo: 'Informations du Compte',
      accountUsername: 'Nom d\'Utilisateur / Email du Compte',
      accountUsernamePlaceholder: 'Email du compte de service',
      accountUsernameRequired: 'Le nom d\'utilisateur du compte est requis',
      accountPassword: 'Mot de Passe du Compte',
      accountPasswordPlaceholder: 'Mot de passe du compte de service',
      accountPasswordRequired: 'Le mot de passe du compte est requis',
      // Actions
      cancel: 'Annuler',
      saveChanges: 'Enregistrer les Modifications',
      edit: 'Modifier',
      remove: 'Supprimer',
      // Empty State
      noAddresses: 'Aucune adresse enregistrée',
      noAddressesDesc: 'Ajoutez une adresse pour commencer',
      // Labels
      account: 'Compte',
      email: 'Email',
    },

    // ==================== RECEIPT UPLOAD PAGE ====================
    receipt: {
      pageTitle: 'Télécharger le Reçu de Paiement',
      orderTotal: 'Total de la Commande',
      orderId: 'ID de Commande',
      items: 'Articles',
      itemsCount: 'article(s)',
      // Instructions
      instructionsTitle: 'Instructions de Paiement',
      instruction1: 'Effectuez le paiement sur le compte spécifié',
      instruction2: 'Prenez une capture d\'écran claire ou une photo de votre reçu de paiement',
      instruction3: 'Téléchargez l\'image du reçu ci-dessous',
      instruction4: 'Attendez que notre équipe vérifie votre paiement',
      // Upload Area
      dragDrop: 'Glissez-déposez votre reçu ici',
      orClickBrowse: 'ou cliquez pour parcourir',
      supportedFormats: 'Formats supportés : JPG, PNG, WEBP (Max 5 Mo)',
      removeChooseAnother: 'Supprimer et Choisir un Autre',
      // Errors
      selectImageError: 'Veuillez sélectionner un fichier image (JPG, PNG, etc.)',
      fileSizeError: 'La taille du fichier doit être inférieure à 5 Mo',
      selectReceiptError: 'Veuillez d\'abord sélectionner une image de reçu',
      uploadFailed: 'Échec du téléchargement du reçu',
      networkError: 'Erreur réseau. Veuillez réessayer.',
      // Buttons
      uploading: 'Téléchargement en cours...',
      uploadButton: 'Télécharger le Reçu et Finaliser la Commande',
      backToCheckout: 'Retour au Paiement',
      // Security Note
      securityNote: 'Votre reçu sera stocké en toute sécurité et examiné par notre équipe dans les 24 heures.',
      // Success
      successTitle: 'Reçu Téléchargé avec Succès !',
      successDesc: 'Votre reçu de paiement a été soumis pour vérification. Nous vous informerons une fois vérifié.',
      redirecting: 'Redirection vers la confirmation de commande...',
    },

    // ==================== ORDER SUCCESS PAGE ====================
    orderSuccess: {
      pageTitle: 'Commande Soumise !',
      orderNumber: 'Commande',
      thankYouMessage: 'Merci pour votre commande. Votre reçu de paiement a été soumis et est en attente de vérification par notre équipe.',
      whatsNext: 'Quelle est la Suite ?',
      step1: 'Notre équipe vérifiera votre reçu de paiement dans les 24 heures.',
      step2: 'Vous recevrez une notification par email une fois votre paiement vérifié.',
      step3: 'Après vérification, votre abonnement sera activé et livré.',
      step4: 'Suivez le statut de votre commande dans Mon Compte > Mes Commandes.',
      backToHome: 'Retour à l\'Accueil',
      viewMyOrders: 'Voir Mes Commandes',
      supportNote: 'Des questions ? Contactez notre équipe de support pour obtenir de l\'aide',
    },

    // ==================== COMMON ELEMENTS ====================
    common: {
      loading: 'Chargement...',
      error: 'Erreur',
      success: 'Succès',
      save: 'Enregistrer',
      cancel: 'Annuler',
      delete: 'Supprimer',
      edit: 'Modifier',
      add: 'Ajouter',
      remove: 'Supprimer',
      close: 'Fermer',
      confirm: 'Confirmer',
      back: 'Retour',
      next: 'Suivant',
      submit: 'Soumettre',
      search: 'Rechercher',
      filter: 'Filtrer',
      sort: 'Trier',
      all: 'Tout',
      yes: 'Oui',
      no: 'Non',
      or: 'ou',
      and: 'et',
      required: 'Requis',
      optional: 'Optionnel',
    },
  },
};

export const useCommonTranslations = (language = 'en') => {
  return commonTranslations[language] || commonTranslations['en'];
};
